import React, { FC, useMemo } from "react";
import { CampaignCardProps } from "../ActionItem";
import { TransactionsUpdateMetaDto } from "../../../common/dto/actions/transactions-update-meta.dto";
import { TRANSFER_MATCH_ACTION } from "../../../common/requiredActions";
import { pluralize } from "../../../common/helpers/string";
import { TransactionsFilters } from "../../transactions/filters/lib";
import { WithRequiredProperties } from "../../../common/types/base/generics";
import { TransactionsUpdateActionCard } from "../common/TransactionsUpdateActionCard";
import { LinkIcon } from "../../../icons";
import {
    TransactionColumn,
    TransactionView,
} from "../../../common/types/views/transactionViews";
import { getActionCadenceHeader, getActionDateRange } from "../helpers";

export const TransferMatchTransactionsCard: FC<
    CampaignCardProps<TransactionsUpdateMetaDto>
> = (props) => {
    const { action } = props;

    const transactionFilters: WithRequiredProperties<
        TransactionsFilters,
        "entityIds"
    > = useMemo(() => {
        const dateRange = getActionDateRange(action.date, action.cadence);
        return {
            entityIds: action.entity ? [action.entity.id] : [],
            requiredActions: [TRANSFER_MATCH_ACTION],
            ...(dateRange ?? {}),
        };
    }, [action.entity, action.date, action.cadence]);

    const getSectionTitle = (count: number) => {
        if (count === 0) {
            return <>You're all set with your transactions for now</>;
        }
        return (
            <>
                {count} {pluralize("transfer", count)} need
                {count === 1 ? "s" : ""} a match for tax substantiation
            </>
        );
    };

    const view: TransactionView = useMemo(
        () => ({
            type: "transaction" as const,
            columns: [
                { id: TransactionColumn.LOCK, options: { pin: "left" } },
                { id: TransactionColumn.DATE, options: { pin: "left" } },
                { id: TransactionColumn.DESCRIPTION },
                { id: TransactionColumn.CATEGORY },
                { id: TransactionColumn.ACCOUNT },
                { id: TransactionColumn.AMOUNT },
                {
                    id: TransactionColumn.ENTITY,
                    options: { pin: "right", header: "" },
                },
                {
                    id: TransactionColumn.ACTIONS,
                    options: {
                        pin: "right",
                        requiredActions: [TRANSFER_MATCH_ACTION],
                    },
                },
                {
                    id: TransactionColumn.TRANSFER_MATCH,
                    options: { pin: "right" },
                },
            ],
            filters: transactionFilters,
        }),
        [transactionFilters],
    );

    return (
        <TransactionsUpdateActionCard
            {...props}
            pendingActionHeader={`Find matches for your ${getActionCadenceHeader(action.date, action.cadence)} transfers`}
            completedActionHeader={`Transactions Matched`}
            getTransactionsSectionTitle={getSectionTitle}
            icon={<LinkIcon />}
            transactionFilters={transactionFilters}
            view={view}
        />
    );
};
